/**
 * The ready-made starts New automation offers (c-build/v-autos.js `newAuto`): each is a real flow, written with
 * fill-ins so it reads right on any room, that the host then edits like any other.
 */
import type { StopWhen, TriggerJson } from "../../shared/types";
import { CUSTOMER_TAG } from "../../shared/offer";
import { newStep, type Step } from "./flow-model";

export interface Start {
  key: "reminder" | "replay" | "offer" | "thanks" | "blank";
  title: string;
  line: string;
  /** The automation's name when it is made. */
  name: string;
  trigger: TriggerJson;
  stopWhen: StopWhen;
  steps: () => Step[];
}

const wait = (mode: "after" | "before", num: number, unit: "minutes" | "hours" | "days"): Step => ({ ...newStep("wait"), mode, num, unit }) as Step;

function email(subject: string, paragraphs: string[], button: string): Step {
  const base = newStep("email");
  return {
    ...base,
    subject,
    preview: paragraphs.join("\n\n"),
    previewHtml: paragraphs.map((p) => `<p>${p}</p>`).join(""),
    button,
  } as Step;
}

export const STARTS: readonly Start[] = [
  {
    key: "reminder",
    title: "Reminder before a live session",
    line: "An email 1 day and 1 hour before.",
    name: "Before the session",
    trigger: { type: "tag_added", value: "registered", how_often: "each" },
    stopWhen: null,
    steps: () => [
      wait("before", 1, "days"),
      email(
        "Tomorrow: {{room title}}",
        ["Hi {{first name}},", "We’re live tomorrow, {{session time}}. Bring one question and I’ll answer it in the room.", "{{host}}"],
        "Open your page",
      ),
      wait("before", 1, "hours"),
      email("We start in an hour", ["Hi {{first name}},", "We start in an hour. The room opens a few minutes early, so come in and say hello.", "{{host}}"], "Join the room"),
    ],
  },
  {
    key: "replay",
    title: "Replay nudge",
    line: "For people who registered but didn’t watch.",
    name: "Replay nudge",
    trigger: { type: "tag_added", value: "registered" },
    stopWhen: { signal: "has_tag", tag: "watched" },
    steps: () => [
      wait("after", 1, "days"),
      email(
        "Your replay is waiting",
        ["Hi {{first name}},", "You saved a seat for {{room title}}. It’s on your page whenever you have a few minutes, and it keeps your place if you stop.", "{{host}}"],
        "Watch it now",
      ),
    ],
  },
  {
    key: "offer",
    title: "Offer follow-up",
    line: "For people who clicked the offer but didn’t sign up.",
    name: "Offer follow-up",
    trigger: { type: "tag_added", value: "clicked-offer" },
    stopWhen: { signal: "has_tag", tag: CUSTOMER_TAG },
    steps: () => [
      wait("after", 1, "days"),
      email(
        "Still thinking it over?",
        ["Hi {{first name}},", "Yesterday you had a look at my offer. If a question is holding you back, just reply to this email.", "{{host}}"],
        "See the offer",
      ),
    ],
  },
  {
    key: "thanks",
    title: "Thank you after finishing",
    line: "A personal note when someone watches to the end.",
    name: "Thank you",
    trigger: { type: "tag_added", value: "completed" },
    stopWhen: null,
    steps: () => [
      email(
        "Thank you for staying to the end",
        ["Hi {{first name}},", "Thank you for watching {{room title}} to the end. Everything from it stays on your page.", "{{host}}"],
        "Open your page",
      ),
    ],
  },
  {
    key: "blank",
    title: "Start from nothing",
    line: "",
    name: "New automation",
    trigger: { type: "tag_added", value: "registered" },
    stopWhen: null,
    steps: () => [],
  },
];
